import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { isNil } from "lodash";
import type React from "react";
import { useState } from "react";

/**
 * The identity + retrieval-rank columns every exemplar list has, whichever tool
 * it came from. `rank` is optional: a source that only guarantees relevance
 * ORDER (the ad-hoc run's exemplars) omits it, and the column is then hidden.
 */
export type ExemplarRow = {
  readonly documentId: string;
  readonly documentType: string;
  readonly source: string;
  readonly cosineSimilarity: number;
  readonly rank?: number;
};

const styles = {
  paper: { p: 2 },
  dialogTitle: {
    fontFamily: "monospace",
    fontSize: 14,
  },
} satisfies Record<string, SxProps<Theme>>;

/** Cosine similarity as a percentage plus the raw value to three decimals. */
const formatSimilarity = (cosineSimilarity: number): string =>
  `${(cosineSimilarity * 100).toFixed(1)}% (${cosineSimilarity.toFixed(3)})`;

/** One exemplar row with a source-viewer drill-in. */
const ExemplarTableRow = <Row extends ExemplarRow>({
  exemplar,
  showRank,
  renderViewer,
  renderDetail,
}: {
  readonly exemplar: Row;
  readonly showRank: boolean;
  readonly renderViewer: (exemplar: Row) => React.ReactNode;
  readonly renderDetail?: (exemplar: Row) => React.ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const hasDetail = !isNil(renderDetail);
  return (
    <>
      <TableRow hover>
        {showRank && <TableCell align="right">{exemplar.rank}</TableCell>}
        <TableCell>{exemplar.documentType}</TableCell>
        <TableCell>
          <Chip size="small" variant="outlined" label={exemplar.source} />
        </TableCell>
        <TableCell align="right">
          {formatSimilarity(exemplar.cosineSimilarity)}
        </TableCell>
        <TableCell align="center">
          <Tooltip title="View exemplar source">
            <IconButton
              size="small"
              aria-label="View exemplar source"
              onClick={() => {
                setOpen(true);
              }}
            >
              <ImageSearchIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>

      {/* Mounted only while open, so the source + viewer are fetched lazily. */}
      <Dialog
        fullWidth
        maxWidth="xl"
        open={open}
        onClose={() => {
          setOpen(false);
        }}
      >
        <DialogTitle sx={styles.dialogTitle}>{exemplar.documentId}</DialogTitle>
        <DialogContent dividers>
          {open && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={hasDetail ? 7 : 12}>
                {renderViewer(exemplar)}
              </Grid>
              {hasDetail && (
                <Grid item xs={12} md={5}>
                  {renderDetail(exemplar)}
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpen(false);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

/**
 * Ranked list of the exemplars that shaped an extraction, in the order given
 * (already relevance-descending from the producer). Each row drills into the
 * exemplar's source document, so an operator reads the actual paperwork the
 * few-shot prompt was built from rather than a list of ids.
 *
 * Presentational and tool-agnostic: the caller supplies the viewer, because how
 * an exemplar's source is reached differs per tool (a backend passthrough route
 * for an ad-hoc run, a pre-signed S3 URL for a production scan log).
 *
 * @param props The component props.
 * @param props.exemplars The exemplars, in the order to render them.
 * @param props.renderViewer Renders one exemplar's source document. Called only
 *   while that row's drill-in is open.
 * @param props.renderDetail Renders anything shown beside the document (e.g. the
 *   exemplar's corrected field values). Omit for a source-only drill-in, and the
 *   viewer takes the full width.
 * @param props.emptyMessage Shown instead of the table when there are none.
 */
const ExemplarPanel = <Row extends ExemplarRow>({
  exemplars,
  renderViewer,
  renderDetail,
  emptyMessage = "No exemplars were retrieved for this document.",
}: {
  readonly exemplars: readonly Row[];
  readonly renderViewer: (exemplar: Row) => React.ReactNode;
  readonly renderDetail?: (exemplar: Row) => React.ReactNode;
  readonly emptyMessage?: string;
}) => {
  // Only show the rank column when the producer records ranks; the ad-hoc run's
  // exemplars carry relevance order alone.
  const showRank = exemplars.some((exemplar) => !isNil(exemplar.rank));
  return (
    <Paper variant="outlined" sx={styles.paper}>
      <Typography gutterBottom variant="h6">
        Exemplars ({exemplars.length})
      </Typography>
      {exemplars.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              {showRank && (
                <Tooltip title="Rank in the relevance-descending selection (1 = most relevant).">
                  <TableCell align="right">Rank</TableCell>
                </Tooltip>
              )}
              <TableCell>Document type</TableCell>
              <TableCell>Source</TableCell>
              <Tooltip title="Cosine similarity of the exemplar to the query document (retrieval rank).">
                <TableCell align="right">Similarity</TableCell>
              </Tooltip>
              <TableCell align="center">View</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {exemplars.map((exemplar) => (
              <ExemplarTableRow
                key={exemplar.documentId}
                exemplar={exemplar}
                showRank={showRank}
                renderViewer={renderViewer}
                renderDetail={renderDetail}
              />
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
};

export default ExemplarPanel;
